import useSWR from 'swr';
import { useSearchParams } from 'react-router-dom';
import { fetcher } from '../../services/fetcher';

const API_LIST = '/payables';
const API_DETAIL = (id: number) => `/payables/${id}`;

/** Use from Payables List so status filter from URL (?status=pending|paid|overdue|all) is applied.
 * Default (no status or pending) = unpaid queue. */
export const usePayablesWithStatusFilter = (searchParams?: string) => {
  const [searchParamsObj] = useSearchParams();
  const status = searchParamsObj.get('status');
  const filter =
    status == null || status === '' || status === 'pending'
      ? 'pending'
      : status === 'all'
        ? undefined
        : status;
  return usePayables(searchParams, filter);
};

export interface Payable {
  id: number;
  payeeId?: number;
  payeeName: string;
  invoiceDate?: string;
  dueDate?: string;
  amount: number;
  status: string;
  paymentDate?: string;
  notes?: string;
  categoryId?: number;
  subCategoryId?: number;
  createdAt?: string;
  updatedAt?: string;
  createdById?: number;
  updatedById?: number;
  categoryDescr?: { id: number; name: string };
  subCategoryDescr?: { id: number; name: string };
  createdBy?: { userFirstName: string; userLastName?: string };
  updatedBy?: { userFirstName: string; userLastName?: string };
  FileAttachment?: Array<{
    id: number;
    fileName: string;
    filePath: string;
    fileType: string;
    fileSize: number;
    uploadedAt: string;
  }>;
}

export interface PayablesListResponse {
  records: Payable[];
}

export const usePayables = (searchParams?: string, filter?: string) => {
  let query = '';
  if (searchParams) query += `?searchParams=${encodeURIComponent(searchParams)}`;
  if (filter) query += query ? `&filter=${encodeURIComponent(filter)}` : `?filter=${encodeURIComponent(filter)}`;

  const { data, error, isLoading, mutate } = useSWR<PayablesListResponse>(`${API_LIST}${query}`, fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });

  const records = data?.records ?? [];
  return {
    data: records,
    records,
    isLoading,
    isError: !!error,
    mutate,
  };
};

export const usePayable = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Payable>(id ? API_DETAIL(id) : null, fetcher, {
    revalidateOnFocus: true,
  });
  return {
    data: data,
    payable: data,
    isLoading,
    isError: !!error,
    mutate,
  };
};

export const useExpenseCategoriesForPayable = () => {
  const { data, error, isLoading } = useSWR<any[]>('/crm-expense-category', fetcher);
  const options = (data || []).map((c) => ({ value: String(c.id), label: c.name }));
  return { options, isLoading, isError: !!error };
};
